import React, { useEffect, useMemo, useState } from "react";
import { PRESETS, WIDTH, HEIGHT } from "@hudhud/reel";
import { LANG_NAMES, type CaptionStyle } from "@hudhud/core";
import { Icon } from "../components/Icon.js";
import { useEditor } from "./store.js";
import { Captions } from "./PlayerLayers.js";

const SAMPLE = "Allah is near to those who call on Him";
const SAMPLE_AR = "إن الله قريب ممن يدعوه";
const SAMPLE_WORDS = SAMPLE.split(" ").map((text, i) => ({ text, startMs: i * 380, endMs: i * 380 + 340, emphasis: i === 1 }));
const STAGE_H = 640; // composition px shown in a tile: enough for two lines of the biggest look
const TICK_MS = 340;

/** How light a colour is, 0..1. */
const lum = (hex: string): number => { const m = hex.replace("#", ""); const n = parseInt(m.length === 3 ? m.split("").map((c) => c + c).join("") : m, 16); return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255; };
/** A neutral ground the look reads on: charcoal under light words, warm paper under dark ones; never the brand green, which fights green and gold highlights. */
export const groundFor = (style: CaptionStyle): string => {
  const boxed = style.box !== "none" && style.boxOpacity >= 0.5;
  const ink = boxed ? lum(style.boxColor) : lum(style.color);
  return ink > 0.55 ? "radial-gradient(ellipse at 50% 42%, #3d403d 0%, #22252a 60%, #15171a 100%)" : "radial-gradient(ellipse at 50% 42%, #f6f2ea 0%, #e6e0d4 60%, #d3ccbd 100%)";
};

/** The card as it would look, playing: the spoken word moves along the words and the card comes in again each round. */
export const LiveCaption: React.FC<{ style: CaptionStyle; width: number; play?: boolean; second?: boolean }> = ({ style, width, play = true, second }) => {
  const n = Math.max(1, Math.min(SAMPLE_WORDS.length, style.wordsPerCard));
  const card = useMemo(() => ({ id: "sample", startMs: 0, endMs: n * TICK_MS, words: SAMPLE_WORDS.slice(0, n) }), [n]);
  const [tick, setTick] = useState(0);
  useEffect(() => { if (!play) return; const t = setInterval(() => setTick((x) => x + 1), TICK_MS); return () => clearInterval(t); }, [play]);
  const active = play ? tick % (n + 2) : Math.min(1, n - 1); // two beats of rest between rounds
  const round = Math.floor(tick / (n + 2));
  const scale = width / WIDTH;
  const centred = { ...style, yPos: 0.5, xOffset: 0 };
  return (
    <div className="livecap" style={{ width, height: Math.round(STAGE_H * scale), background: groundFor(style) }} aria-hidden="true">
      {/* the caption layer places itself at the frame's mid-height; the stage is a full frame slid up so that point sits in the middle of the tile */}
      <div className="livecap-stage" style={{ width: WIDTH, height: HEIGHT, transform: `scale(${scale})`, top: -Math.round((HEIGHT / 2 - STAGE_H / 2) * scale) }}>
        <div key={round} className={`livecap-inner in-${style.animation}`} style={{ position: "absolute", left: 0, top: 0, width: WIDTH, height: HEIGHT }}>
          <Captions card={card} style={{ ...centred, yPos: 0.5 }} active={Math.min(active, n - 1)} lang="en" second={second && style.secondLang ? { text: SAMPLE_AR, font: "Cairo", rtl: true } : null} />
        </div>
      </div>
    </div>
  );
};

const CATS: { key: string; label: string; test: (p: CaptionStyle) => boolean }[] = [
  { key: "all", label: "All", test: () => true },
  { key: "karaoke", label: "Karaoke", test: (p) => p.highlightMode === "color" || p.highlightMode === "underline" },
  { key: "pill", label: "Pill", test: (p) => p.highlightMode === "pill" },
  { key: "boxed", label: "Boxed", test: (p) => p.box !== "none" },
  { key: "big", label: "Big & bold", test: (p) => p.fontSize >= 72 || p.caseMode === "upper" },
  { key: "soft", label: "Soft", test: (p) => p.box === "none" && p.strokeWidth === 0 && p.caseMode !== "upper" },
];
const tags = (p: CaptionStyle): string[] => [
  `${p.wordsPerCard} words`,
  p.highlightMode === "pill" ? "pill" : p.highlightMode === "glow" ? "glow" : p.highlightMode === "underline" ? "underline" : p.highlightMode === "none" ? "still" : "colour",
  p.box !== "none" ? `${p.box}` : p.strokeWidth > 0 ? "outline" : "clean",
  p.animation !== "none" ? p.animation : "",
].filter(Boolean);
const nameOf = (p: CaptionStyle) => p.preset.replace(/^\w/, (m) => m.toUpperCase());

/**
 * Every caption look, playing. Tap one and the clip takes it (the second line and its language stay as they were). Lives inside the
 * settings column on the desk and inside the sheet on a phone, so the video stays in view.
 */
export const CaptionGallery: React.FC<{ columns?: number }> = ({ columns }) => {
  const clip = useEditor((s) => s.clip);
  const update = useEditor((s) => s.update);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [width, setWidth] = useState(0);
  const gridRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => { const el = gridRef.current; if (!el) return; const ro = new ResizeObserver(() => setWidth(el.clientWidth)); ro.observe(el); setWidth(el.clientWidth); return () => ro.disconnect(); }, []);
  if (!clip) return null;
  const cols = columns ?? (width >= 520 ? 3 : 2);
  const tileW = width ? Math.floor((width - (cols - 1) * 10) / cols) : 160;
  const list = Object.values(PRESETS).filter((p) => (CATS.find((c) => c.key === cat)?.test ?? (() => true))(p)).filter((p) => !q.trim() || `${nameOf(p)} ${tags(p).join(" ")} ${p.font}`.toLowerCase().includes(q.toLowerCase()));
  const current = clip.style.preset;
  const use = (p: CaptionStyle) => update((c) => ({ ...c, style: { ...p, secondLang: c.style.secondLang, secondScale: c.style.secondScale, secondColor: c.style.secondColor } }));
  return (
    <div className="gallery">
      <div className="gallery-head">
        <div className="gallery-search"><Icon name="search" size={14} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search looks… bold, pill, serif" aria-label="Search caption looks" /></div>
        <div className="gallery-cats" role="tablist" aria-label="Kinds of look">{CATS.map((c) => <button key={c.key} role="tab" aria-selected={cat === c.key} className={cat === c.key ? "on" : ""} onClick={() => setCat(c.key)}>{c.label}</button>)}</div>
      </div>
      <div ref={gridRef} className="gallery-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {list.map((p) => {
          const on = current === p.preset;
          return (
            <div key={p.preset} className={`gtile${on ? " on" : ""}`} role="button" tabIndex={0} onClick={() => use(p)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); use(p); } }} aria-pressed={on} aria-label={`${nameOf(p)} look${on ? ", in use" : ""}`}>
              <LiveCaption style={p} width={tileW - 2} second={!!clip.style.secondLang && !!clip.versions[clip.style.secondLang as keyof typeof clip.versions]} />
              <span className="gtile-name">{nameOf(p)}{on ? <b>In use</b> : null}</span>
              <span className="gtile-tags">{tags(p).join(" · ")}</span>
            </div>
          );
        })}
        {!list.length ? <span className="hint" style={{ gridColumn: "1 / -1", padding: 20, textAlign: "center" }}>No look matches that.</span> : null}
      </div>
      {clip.style.secondLang && !clip.versions[clip.style.secondLang as keyof typeof clip.versions] ? <span className="hint" style={{ padding: "0 12px 10px" }}>The {LANG_NAMES[clip.style.secondLang as keyof typeof LANG_NAMES] ?? clip.style.secondLang} line shows once that translation is made (Captions → Second line).</span> : null}
    </div>
  );
};
